/**
 * WritingAssessmentResults Component
 *
 * Displays comprehensive AI assessment feedback for student essays.
 * Shows overall band, criteria scores, strengths, improvements, and example edits.
 */

import React, { useState } from 'react';
import {
  ChartColumn,
  Check,
  CircleCheckBig,
  ClipboardList,
  MessageSquare,
  Pencil,
  Printer,
  Sparkles,
  Star,
  Target,
  X,
} from 'lucide-react';
import './WritingAssessmentResults.css';

const CRITERION_LABELS = {
  content: 'Content',
  structure: 'Structure',
  vocabulary: 'Vocabulary',
  grammar: 'Grammar',
  audience: 'Audience',
  engagement: 'Engagement'
};

const BAND_INFO = {
  'Outstanding': { color: '#10b981', icon: <Sparkles size={16} className="inline-icon" />, label: 'Outstanding', range: '9–10' },
  'High Achievement': { color: '#3b82f6', icon: <Star size={16} className="inline-icon" />, label: 'High Achievement', range: '7–8' },
  'Sound': { color: '#f59e0b', icon: <Check size={16} className="inline-icon" />, label: 'Sound', range: '5–6' },
  'Basic': { color: '#f97316', icon: '○', label: 'Basic', range: '3–4' },
  'Limited': { color: '#ef4444', icon: '△', label: 'Limited', range: '1–2' }
};

const DEFAULT_BAND = BAND_INFO['Sound'];

const WritingAssessmentResults = ({ assessment, onClose }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!assessment) {
    return null;
  }

  const {
    overall_score,
    band_level,
    word_count,
    criteria_scores,
    strengths,
    improvements,
    example_edits,
    assessed_at
  } = assessment;

  const bandInfo = BAND_INFO[band_level] || DEFAULT_BAND;
  const percentage = Math.round((overall_score ?? 0) * 10);

  const criteriaList = criteria_scores
    ? Object.entries(criteria_scores).map(([key, data]) => ({
        key,
        name: CRITERION_LABELS[key] || (key.charAt(0).toUpperCase() + key.slice(1)),
        score: data.score,
        weight: data.weight,
        feedback: data.feedback
      }))
    : [];

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="assessment-results">
      {/* Header with overall score */}
      <div className="results-header">
        <button className="close-button" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>

        <div className="overall-score">
          <div
            className="score-circle"
            style={{ borderColor: bandInfo.color }}
          >
            <div className="score-value" style={{ color: bandInfo.color }}>
              {percentage}%
            </div>
            <div className="score-label">
              {(overall_score ?? 0).toFixed(1)} / 10
            </div>
          </div>

          <div className="band-info">
            <div className="band-badge" style={{ backgroundColor: bandInfo.color }}>
              <span className="band-icon">{bandInfo.icon}</span>
              <span className="band-label">{bandInfo.label}</span>
            </div>
            <div className="band-range">Band {bandInfo.range}</div>
          </div>
        </div>

        <div className="assessment-meta">
          <span className="meta-label">Assessed:</span>
          <span className="meta-value">{formatDate(assessed_at)}</span>
          {typeof word_count === 'number' && (
            <>
              <span className="meta-label" style={{ marginLeft: '1rem' }}>Words:</span>
              <span className="meta-value">{word_count}</span>
            </>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="results-tabs">
        <button
          className={`tab-button ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          <ChartColumn size={16} className="inline-icon" /> Overview
        </button>
        <button
          className={`tab-button ${activeTab === 'criteria' ? 'active' : ''}`}
          onClick={() => setActiveTab('criteria')}
        >
          <ClipboardList size={16} className="inline-icon" /> Criteria
        </button>
        <button
          className={`tab-button ${activeTab === 'feedback' ? 'active' : ''}`}
          onClick={() => setActiveTab('feedback')}
        >
          <MessageSquare size={16} className="inline-icon" /> Feedback
        </button>
        {example_edits && example_edits.length > 0 && (
          <button
            className={`tab-button ${activeTab === 'edits' ? 'active' : ''}`}
            onClick={() => setActiveTab('edits')}
          >
            <Pencil size={16} className="inline-icon" /> Examples
          </button>
        )}
      </div>

      {/* Tab Content */}
      <div className="results-content">
        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="tab-panel">
            <div className="feedback-columns">
              <section className="feedback-section strengths-section">
                <h3 className="section-title"><CircleCheckBig size={18} className="inline-icon" /> Strengths</h3>
                <ul className="feedback-list">
                  {(strengths || []).map((strength, index) => (
                    <li key={index} className="feedback-item strength-item">
                      {strength}
                    </li>
                  ))}
                </ul>
              </section>

              <section className="feedback-section improvements-section">
                <h3 className="section-title"><Target size={18} className="inline-icon" /> Areas for Improvement</h3>
                <ul className="feedback-list">
                  {(improvements || []).map((improvement, index) => (
                    <li key={index} className="feedback-item improvement-item">
                      {improvement}
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        )}

        {/* Criteria Tab */}
        {activeTab === 'criteria' && (
          <div className="tab-panel">
            <section className="feedback-section">
              <h3 className="section-title">Detailed Criteria Scores</h3>
              <div className="criteria-scores">
                {criteriaList.map((criterion) => {
                  const criterionPercentage = Math.round((criterion.score ?? 0) * 10);
                  const criterionColor =
                    criterionPercentage >= 75
                      ? '#10b981'
                      : criterionPercentage >= 60
                      ? '#f59e0b'
                      : '#ef4444';

                  return (
                    <div key={criterion.key} className="criteria-card">
                      <div className="criteria-header">
                        <h4 className="criteria-name">{criterion.name}</h4>
                        <div
                          className="criteria-score"
                          style={{ color: criterionColor }}
                        >
                          {(criterion.score ?? 0).toFixed(1)} / 10
                        </div>
                      </div>

                      <div className="criteria-progress">
                        <div
                          className="criteria-progress-bar"
                          style={{
                            width: `${criterionPercentage}%`,
                            backgroundColor: criterionColor
                          }}
                        />
                      </div>

                      <div className="criteria-band">
                        Weight: <strong>{criterion.weight}%</strong>
                      </div>

                      <p className="criteria-feedback">{criterion.feedback}</p>
                    </div>
                  );
                })}
              </div>
            </section>
          </div>
        )}

        {/* Feedback Tab */}
        {activeTab === 'feedback' && (
          <div className="tab-panel">
            <section className="feedback-section">
              <h3 className="section-title">What You Did Well</h3>
              <div className="feedback-cards">
                {(strengths || []).map((strength, index) => (
                  <div key={index} className="feedback-card strength-card">
                    <div className="card-icon">✓</div>
                    <div className="card-text">{strength}</div>
                  </div>
                ))}
              </div>
            </section>

            <section className="feedback-section">
              <h3 className="section-title">How to Improve</h3>
              <div className="feedback-cards">
                {(improvements || []).map((improvement, index) => (
                  <div key={index} className="feedback-card improvement-card">
                    <div className="card-icon">→</div>
                    <div className="card-text">{improvement}</div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}

        {/* Example Edits Tab */}
        {activeTab === 'edits' && example_edits && example_edits.length > 0 && (
          <div className="tab-panel">
            <section className="feedback-section">
              <h3 className="section-title">Example Edits</h3>
              <p className="section-description">
                Here are some specific examples showing how you could improve your writing:
              </p>

              <div className="example-edits">
                {example_edits.map((edit, index) => (
                  <div key={index} className="edit-card">
                    <div className="edit-header">
                      <span className="edit-label">Example {index + 1}</span>
                    </div>

                    <div className="edit-comparison">
                      <div className="edit-before">
                        <div className="edit-label-text">Before</div>
                        <div className="edit-text original">
                          "{edit.before}"
                        </div>
                      </div>

                      <div className="edit-arrow">→</div>

                      <div className="edit-after">
                        <div className="edit-label-text">After</div>
                        <div className="edit-text improved">
                          "{edit.after}"
                        </div>
                      </div>
                    </div>

                    <div className="edit-explanation">
                      <strong>Why this is better:</strong> {edit.reasoning}
                    </div>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div className="results-footer">
        <button className="action-button secondary" onClick={onClose}>
          Close
        </button>
        <button
          className="action-button primary"
          onClick={() => window.print()}
        >
          <Printer size={16} className="inline-icon" /> Print Report
        </button>
      </div>
    </div>
  );
};

export default WritingAssessmentResults;
